// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelHead, PanelBody, Input, Button, Spinner } from '../components/ui';
import { updateBeach } from '../services/firebase';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { useWindowSize } from '../hooks/useWindowSize';
import { callFunction } from '../utils/cloudFunctions';

// ─── Upload immagine su Firebase Storage ─────────────────────────────────────
async function uploadImage(beachCode, file, onProgress) {
  const storage = getStorage();
  const ext = file.name.split('.').pop();
  const path = `lidi/${beachCode}/gallery/${Date.now()}.${ext}`;
  const storageRef = ref(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      snap => onProgress && onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      reject,
      async () => { resolve({ url: await getDownloadURL(task.snapshot.ref), path }); }
    );
  });
}

async function deleteImage(path) {
  if (!path) return;
  try {
    const storage = getStorage();
    await deleteObject(ref(storage, path));
  } catch (e) { /* già eliminata */ }
}

// ─── Componente galleria ──────────────────────────────────────────────────────
function GalleriaSection({ beachCode, galleria = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const nuove = [...galleria];
      for (const file of Array.from(files)) {
        // Validazione
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 5 * 1024 * 1024) { alert(`${file.name} supera i 5MB`); continue; }
        const result = await uploadImage(beachCode, file, setProgress);
        nuove.push({ url: result.url, path: result.path });
        setProgress(0);
      }
      onChange(nuove);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleRemove = async (idx) => {
    if (!window.confirm('Rimuovere questa foto?')) return;
    const item = galleria[idx];
    await deleteImage(item.path);
    onChange(galleria.filter((_, i) => i !== idx));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const arr = [...galleria];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    onChange(arr);
  };

  const moveDown = (idx) => {
    if (idx === galleria.length - 1) return;
    const arr = [...galleria];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    onChange(arr);
  };

  // Normalizza: supporta sia string (vecchio) che { url, path } (nuovo)
  const getUrl = (item) => typeof item === 'string' ? item : item?.url;

  return (
    <div>
      {/* Suggerimenti dimensioni */}
      <div style={{
        background: 'rgba(31,147,224,0.08)', border: '1px solid rgba(31,147,224,0.2)',
        borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '16px',
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ocean)', marginBottom: '6px' }}>
          📐 Dimensioni consigliate
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.7 }}>
          <strong style={{ color: 'var(--text-soft)' }}>Hero / Prima foto</strong> — 1200×800px (3:2), usata come sfondo principale in app<br />
          <strong style={{ color: 'var(--text-soft)' }}>Galleria</strong> — 800×600px minimo, qualsiasi formato 4:3 o 16:9<br />
          Formato: JPG o WebP · Max 5MB per foto · La prima foto è la copertina
        </div>
      </div>

      {/* Griglia foto */}
      {galleria.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '10px', marginBottom: '14px',
        }}>
          {galleria.map((item, idx) => (
            <div key={idx} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '4/3', background: 'var(--surface)' }}>
              {/* Badge prima foto */}
              {idx === 0 && (
                <div style={{
                  position: 'absolute', top: '6px', left: '6px', zIndex: 2,
                  background: 'var(--ocean)', color: '#fff',
                  fontSize: '9px', fontWeight: 700, padding: '2px 7px',
                  borderRadius: '10px', letterSpacing: '0.05em',
                }}>
                  COPERTINA
                </div>
              )}
              <img
                src={getUrl(item)}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {/* Overlay azioni */}
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)',
                transition: 'background 0.15s',
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                gap: '4px', padding: '6px',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.45)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
              >
                <button
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  title="Sposta a sinistra"
                  style={{ ...actionBtnStyle, opacity: idx === 0 ? 0.3 : 1 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>chevron_left</span>
                </button>
                <button
                  onClick={() => handleRemove(idx)}
                  title="Rimuovi"
                  style={{ ...actionBtnStyle, background: 'rgba(231,76,60,0.85)' }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>delete</span>
                </button>
                <button
                  onClick={() => moveDown(idx)}
                  disabled={idx === galleria.length - 1}
                  title="Sposta a destra"
                  style={{ ...actionBtnStyle, opacity: idx === galleria.length - 1 ? 0.3 : 1 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>chevron_right</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--ocean)'; }}
        onDragLeave={e => e.currentTarget.style.borderColor = 'var(--border-mid)'}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.borderColor = 'var(--border-mid)';
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: '2px dashed var(--border-mid)', borderRadius: 'var(--radius-md)',
          padding: '24px', textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
          transition: 'border-color 0.15s',
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
        {uploading ? (
          <div>
            <Spinner size={20} />
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
              Caricamento... {progress}%
            </div>
            <div style={{
              height: '4px', background: 'var(--border)', borderRadius: '2px',
              marginTop: '8px', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', background: 'var(--ocean)',
                width: `${progress}%`, transition: 'width 0.2s', borderRadius: '2px',
              }} />
            </div>
          </div>
        ) : (
          <>
            <span className="material-symbols-outlined" style={{ fontSize: '28px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px' }}>
              add_photo_alternate
            </span>
            <div style={{ fontSize: '13px', color: 'var(--text-soft)', fontWeight: 500 }}>
              Clicca o trascina qui le foto
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              JPG, PNG, WebP · Max 5MB · Multiplo supportato
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const actionBtnStyle = {
  width: '26px', height: '26px', borderRadius: '6px',
  background: 'rgba(0,0,0,0.6)', border: 'none',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#fff',
};

// ─── Stripe Connect ───────────────────────────────────────────────────────────

const STRIPE_STATUS = {
  none: {
    icon: '💳',
    label: 'Non configurato',
    color: 'var(--text-soft)',
    bg: 'var(--surface)',
    border: 'var(--border)',
    desc: 'Collega il tuo account Stripe per ricevere i pagamenti direttamente sul tuo conto corrente.',
    btnLabel: 'Collega account Stripe',
    action: 'connect',
  },
  onboarding: {
    icon: '⏳',
    label: 'Configurazione in corso',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.07)',
    border: 'rgba(245,158,11,0.25)',
    desc: 'Completa la verifica dell\'identità su Stripe per attivare i pagamenti.',
    btnLabel: 'Completa verifica KYC →',
    action: 'onboarding',
  },
  in_verifica: {
    icon: '🔍',
    label: 'Verifica in corso',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.07)',
    border: 'rgba(245,158,11,0.25)',
    desc: 'Stripe sta verificando i documenti. Puoi riaprire il link per controllare o aggiungere informazioni.',
    btnLabel: 'Riapri verifica →',
    action: 'onboarding',
  },
  attivo: {
    icon: '✅',
    label: 'Attivo — pagamenti abilitati',
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.07)',
    border: 'rgba(34,197,94,0.25)',
    desc: 'I pagamenti sono attivi. I clienti possono prenotare e pagare direttamente al tuo lido.',
    btnLabel: 'Apri dashboard Stripe',
    action: 'dashboard',
  },
  restricted: {
    icon: '⚠️',
    label: 'Documenti mancanti',
    color: '#f97316',
    bg: 'rgba(249,115,22,0.07)',
    border: 'rgba(249,115,22,0.25)',
    desc: 'Stripe richiede ulteriori informazioni. Completa i documenti per evitare la sospensione dei pagamenti.',
    btnLabel: 'Completa documenti →',
    action: 'onboarding',
  },
  sospeso: {
    icon: '🚫',
    label: 'Account sospeso',
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.07)',
    border: 'rgba(239,68,68,0.25)',
    desc: 'L\'account Stripe risulta sospeso. Se hai interrotto la configurazione, prova a riprenderla. Se il problema persiste contatta il supporto Stripe o scrivi a support@justsalento.com.',
    btnLabel: 'Riprova configurazione →',
    action: 'onboarding',
  },
};

function StripeConnectPanel({ beach, beachCode, refreshBeach }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Quando l'utente torna dalla KYC via refreshUrl (?stripe_refresh=1), aggiorna lo stato
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_refresh') === '1') {
      refreshBeach();
      window.history.replaceState({}, '', '/settings');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasAccount = !!beach?.stripeAccountId;
  const statoKey = !hasAccount ? 'none' : (beach?.stato || 'onboarding');
  const cfg = STRIPE_STATUS[statoKey] || STRIPE_STATUS.none;

  const apriOnboarding = async () => {
    setBusy(true); setErr(null);
    try {
      if (!hasAccount) {
        await callFunction('createConnectedAccount', { lidoId: beachCode });
      }
      const { url } = await callFunction('generateOnboardingLink', {
        lidoId: beachCode,
        returnUrl: `${window.location.origin}/settings`,
        refreshUrl: `${window.location.origin}/settings?stripe_refresh=1`,
      });
      window.open(url, '_blank');
      await refreshBeach();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const apriDashboard = async () => {
    setBusy(true); setErr(null);
    try {
      const { url } = await callFunction('generateLoginLink', { lidoId: beachCode });
      window.open(url, '_blank');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAction = cfg.action === 'dashboard' ? apriDashboard : apriOnboarding;

  return (
    <Panel>
      <PanelHead
        title="Pagamenti Stripe"
        subtitle="Ricevi i pagamenti direttamente sul tuo conto corrente"
      />
      <PanelBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Status badge */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px',
            background: cfg.bg, border: `1px solid ${cfg.border}`,
            borderRadius: 'var(--radius-md)', padding: '14px 16px',
          }}>
            <span style={{ fontSize: '20px', lineHeight: 1, marginTop: '1px' }}>{cfg.icon}</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: cfg.color, marginBottom: '3px' }}>
                {cfg.label}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.55 }}>
                {cfg.desc}
              </div>
            </div>
          </div>

          {/* Requisiti Stripe mancanti */}
          {beach?.requirementsPending?.length > 0 && (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', padding: '12px 14px',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-soft)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                Requisiti mancanti
              </div>
              {beach.requirementsPending.map(r => (
                <div key={r} style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '2px 0', fontFamily: 'monospace' }}>
                  • {r}
                </div>
              ))}
            </div>
          )}

          {/* Errore */}
          {err && (
            <div style={{
              fontSize: '12px', color: '#ef4444',
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 'var(--radius-md)', padding: '10px 12px',
            }}>
              ⚠️ {err}
            </div>
          )}

          {/* Azione principale */}
          {cfg.btnLabel && (
            <Button variant="primary" onClick={handleAction} disabled={busy}>
              {busy ? <Spinner size={14} /> : cfg.btnLabel}
            </Button>
          )}

          {/* Aggiorna stato — utile dopo il ritorno dalla KYC */}
          {hasAccount && statoKey !== 'attivo' && (
            <button
              onClick={() => { refreshBeach(); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '11px', color: 'var(--text-muted)',
                padding: '0', textAlign: 'left', fontFamily: 'var(--font-sans)',
              }}
            >
              ↻ Aggiorna stato
            </button>
          )}

          {/* Nota fee */}
          <div style={{
            fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.6,
            borderTop: '1px solid var(--border)', paddingTop: '12px',
          }}>
            💡 La piattaforma trattiene{' '}
            <strong style={{ color: 'var(--text-soft)' }}>€1,50 a prenotazione</strong>
            {' '}come commissione di servizio, a carico del cliente finale. La fee è non rimborsabile.
          </div>

        </div>
      </PanelBody>
    </Panel>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function Settings() {
  const { beach, admin, refreshBeach } = useAuth();
  const { isMobile } = useWindowSize();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const defaultPolicy = {
    tipoRimborso: 'manuale',
    chiPuoAnnullare: 'entrambi',
    soglieRimborso: { giorniPiena: 7, giorniParziale: 2, percentualeParziale: 50 },
    messaggioPolicy: '',
  };

  const [form, setForm] = useState({
    nome: beach?.nome || '',
    indirizzo: beach?.indirizzo || '',
    telefono: beach?.telefono || '',
    email: beach?.email || '',
    sabbia: beach?.sabbia || '',
    lun_ven: beach?.orari_apertura?.lun_ven || '',
    sab_dom: beach?.orari_apertura?.sab_dom || '',
    galleria: beach?.galleria_fotografica || [],
    speseGestione: beach?.speseGestione ?? 1.00,
    modalitaPrenotazione: beach?.modalitaPrenotazione || { single: true, range: true, multiple: true },
    politica: beach?.politicaCancellazione || defaultPolicy,
    notifiche: beach?.notifiche || { canale: 'telegram', telegramChatId: '', email: '' },
    branding: beach?.branding || { colorePrimario: '#1f93e0' },
  });

  const setNotifiche = (key, val) => setForm(p => ({
    ...p,
    notifiche: { ...p.notifiche, [key]: val }
  }));

  const setPolicy = (key, val) => setForm(p => ({
    ...p,
    politica: { ...p.politica, [key]: val }
  }));

  const setPolicySoglia = (key, val) => setForm(p => ({
    ...p,
    politica: {
      ...p.politica,
      soglieRimborso: { ...p.politica.soglieRimborso, [key]: Number(val) }
    }
  }));

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Normalizza galleria: salva solo gli URL (compatibilità app Angular)
      const galleriaUrls = form.galleria.map(item =>
        typeof item === 'string' ? item : item?.url
      ).filter(Boolean);

      await updateBeach(admin.beachCode, {
        nome: form.nome,
        indirizzo: form.indirizzo,
        telefono: form.telefono,
        email: form.email,
        sabbia: form.sabbia,
        orari_apertura: { lun_ven: form.lun_ven, sab_dom: form.sab_dom },
        galleria_fotografica: galleriaUrls,
        galleria_fotografica_ext: form.galleria,
        // speseGestione: NON salvata da qui. È gestita centralmente dalla
        // piattaforma (coincide con la application_fee Stripe Connect). Il gestore
        // del lido non deve poterla modificare in autonomia — pannello nascosto.
        // Rimuovendola dal payload evitiamo che un salvataggio Settings la
        // sovrascriva con il valore stale del form.
        modalitaPrenotazione: form.modalitaPrenotazione,
        politicaCancellazione: form.politica,
        notifiche: form.notifiche,
        branding: form.branding,
      });
      await refreshBeach();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Topbar
        title="Impostazioni lido"
        actions={
          <Button variant={saved ? 'ghost' : 'primary'} onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size={14} /> : saved ? '✓ Salvato' : 'Salva modifiche'}
          </Button>
        }
      />

      <div style={{ padding: isMobile ? '12px 16px' : '24px 28px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

        <Panel>
          <PanelHead title="Informazioni generali" />
          <PanelBody>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
              <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                <Input label="Nome stabilimento" value={form.nome} onChange={set('nome')} placeholder="Lido Le Dune" />
              </div>
              <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                <Input label="Indirizzo" value={form.indirizzo} onChange={set('indirizzo')} placeholder="Via delle Dune, 15, Avola (SR)" />
              </div>
              <Input label="Telefono" type="tel" value={form.telefono} onChange={set('telefono')} placeholder="+39 0931 112233" />
              <Input label="Email pubblica" type="email" value={form.email} onChange={set('email')} placeholder="info@tuolido.it" />
            </div>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Caratteristiche" />
          <PanelBody>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                  Tipo di sabbia
                </label>
                <select
                  value={form.sabbia}
                  onChange={set('sabbia')}
                  style={{
                    width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)',
                    borderRadius: 'var(--radius-md)', color: '#fff', padding: '9px 12px',
                    fontSize: '13.5px', fontFamily: 'var(--font-sans)',
                  }}
                >
                  <option value="">Seleziona...</option>
                  <option value="sabbia">Sabbia</option>
                  <option value="ciottoli">Ciottoli</option>
                  <option value="misto">Misto</option>
                  <option value="scogliera">Scogliera</option>
                </select>
              </div>
              <Input label="Orari Lun-Ven" value={form.lun_ven} onChange={set('lun_ven')} placeholder="08:30 - 19:30" />
              <Input label="Orari Sab-Dom" value={form.sab_dom} onChange={set('sab_dom')} placeholder="07:30 - 20:00" />
            </div>
          </PanelBody>
        </Panel>

        {/* ── Galleria fotografica ── */}
        <Panel>
          <PanelHead
            title="Galleria fotografica"
            subtitle="Le foto vengono mostrate nella home dell'app"
          />
          <PanelBody>
            <GalleriaSection
              beachCode={admin?.beachCode}
              galleria={form.galleria}
              onChange={galleria => setForm(p => ({ ...p, galleria }))}
            />
          </PanelBody>
        </Panel>

        {/* ── Branding ── */}
        <Panel>
          <PanelHead
            title="Branding"
            subtitle="Personalizza i colori dell'app per il tuo lido"
          />
          <PanelBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Colore primario */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Colore primario (CTA, bottoni, accenti)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  {/* Anteprima colore */}
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px', flexShrink: 0,
                    background: form.branding.colorePrimario,
                    border: '2px solid rgba(255,255,255,0.15)',
                    boxShadow: `0 4px 12px ${form.branding.colorePrimario}55`,
                  }} />
                  {/* Input colore nativo */}
                  <div style={{ flex: 1 }}>
                    <input
                      type="color"
                      value={form.branding.colorePrimario}
                      onChange={e => setForm(p => ({ ...p, branding: { ...p.branding, colorePrimario: e.target.value } }))}
                      style={{ width: '100%', height: '40px', borderRadius: '8px', border: '1px solid var(--border-mid)', cursor: 'pointer', background: 'transparent' }}
                    />
                  </div>
                  {/* Input hex manuale */}
                  <input
                    type="text"
                    value={form.branding.colorePrimario}
                    onChange={e => {
                      const v = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) {
                        setForm(p => ({ ...p, branding: { ...p.branding, colorePrimario: v } }));
                      }
                    }}
                    style={{
                      width: '90px', background: 'var(--surface)', border: '1px solid var(--border-mid)',
                      borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 10px',
                      fontSize: '13px', fontFamily: 'var(--font-sans)', letterSpacing: '0.05em',
                    }}
                  />
                </div>

                {/* Palette preset */}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>Preset</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['#1f93e0', '#e63946', '#2a9d8f', '#e76f51', '#457b9d', '#6a4c93', '#f4a261', '#2d6a4f'].map(c => (
                      <div
                        key={c}
                        onClick={() => setForm(p => ({ ...p, branding: { ...p.branding, colorePrimario: c } }))}
                        style={{
                          width: '28px', height: '28px', borderRadius: '50%', background: c,
                          cursor: 'pointer', flexShrink: 0,
                          border: form.branding.colorePrimario === c ? '3px solid #fff' : '2px solid transparent',
                          transition: 'border 0.1s',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Anteprima bottone */}
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Anteprima
                </div>
                <button style={{
                  padding: '12px 24px', borderRadius: '12px',
                  background: form.branding.colorePrimario,
                  border: 'none', color: '#fff', fontSize: '14px',
                  fontWeight: 600, cursor: 'default', fontFamily: 'var(--font-sans)',
                }}>
                  Prenota ora →
                </button>
              </div>

            </div>
          </PanelBody>
        </Panel>

        {/* ── Notifiche prenotazioni ── */}
        <Panel>
          <PanelHead
            title="Notifiche prenotazioni"
            subtitle="Scegli come ricevere gli avvisi quando arriva una nuova prenotazione"
          />
          <PanelBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Scelta canale */}
              <div style={{ display: 'flex', gap: '10px' }}>

                {/* Telegram — consigliato */}
                <div
                  onClick={() => setNotifiche('canale', 'telegram')}
                  style={{
                    flex: 1, padding: '14px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                    border: `2px solid ${form.notifiche.canale === 'telegram' ? 'var(--ocean)' : 'var(--border-mid)'}`,
                    background: form.notifiche.canale === 'telegram' ? 'rgba(31,147,224,0.08)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '18px' }}>✈️</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Telegram</span>
                    <span style={{
                      fontSize: '9px', fontWeight: 700, padding: '2px 6px',
                      background: 'var(--ocean)', color: '#fff', borderRadius: '8px',
                      letterSpacing: '0.05em',
                    }}>CONSIGLIATO</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Gratuito, istantaneo, nessun limite
                  </div>
                </div>

                {/* Email */}
                <div
                  onClick={() => setNotifiche('canale', 'email')}
                  style={{
                    flex: 1, padding: '14px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                    border: `2px solid ${form.notifiche.canale === 'email' ? 'var(--ocean)' : 'var(--border-mid)'}`,
                    background: form.notifiche.canale === 'email' ? 'rgba(31,147,224,0.08)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '18px' }}>✉️</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Email</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Ricevi una email ad ogni prenotazione
                  </div>
                </div>

              </div>

              {/* Config Telegram */}
              {form.notifiche.canale === 'telegram' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    background: 'rgba(31,147,224,0.06)', border: '1px solid rgba(31,147,224,0.2)',
                    borderRadius: 'var(--radius-md)', padding: '12px 14px',
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ocean)', marginBottom: '8px' }}>
                      📱 Come configurare Telegram
                    </div>
                    <ol style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.8, margin: 0, paddingLeft: '16px' }}>
                      <li>Apri Telegram e cerca <strong style={{ color: 'var(--text-soft)' }}>@justsalento_notify_bot</strong></li>
                      <li>Scrivi <strong style={{ color: 'var(--text-soft)' }}>/start</strong> al bot</li>
                      <li>Apri nel browser: <strong style={{ color: 'var(--text-soft)', wordBreak: 'break-all' }}>api.telegram.org/bot{'<TOKEN>'}/getUpdates</strong></li>
                      <li>Copia il numero <strong style={{ color: 'var(--text-soft)' }}>"id"</strong> dentro <strong style={{ color: 'var(--text-soft)' }}>"chat"</strong> e incollalo qui sotto</li>
                    </ol>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Il tuo Chat ID Telegram
                    </label>
                    <input
                      type="text"
                      placeholder="es. 1655393368"
                      value={form.notifiche.telegramChatId || ''}
                      onChange={e => setNotifiche('telegramChatId', e.target.value)}
                      style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}

              {/* Config Email */}
              {form.notifiche.canale === 'email' && (
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Email per le notifiche
                  </label>
                  <input
                    type="email"
                    placeholder="es. info@tuolido.it"
                    value={form.notifiche.email || ''}
                    onChange={e => setNotifiche('email', e.target.value)}
                    style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
                  />
                </div>
              )}

            </div>
          </PanelBody>
        </Panel>

        {/* ── Spese di gestione ──
            NASCOSTO per ora: la spesa di gestione coincide con la commissione
            piattaforma (application_fee Stripe Connect) ed è gestita centralmente,
            non dal singolo gestore. Riattivare questo pannello solo quando il
            modello sarà definito (es. il gestore può aggiungere un MARKUP proprio
            sopra la commissione piattaforma). Lasciato nel codice apposta. */}
        {false && (
        <Panel>
          <PanelHead
            title="Spese di gestione"
            subtitle="Importo fisso aggiunto ad ogni prenotazione per coprire i costi di servizio"
          />
          <PanelBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ position: 'relative', width: '140px' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: 'var(--sand)', fontWeight: 600 }}>€</span>
                <input
                  type="number" min="0" max="10" step="0.50"
                  value={form.speseGestione}
                  onChange={e => setForm(p => ({ ...p, speseGestione: e.target.value }))}
                  style={{
                    width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)',
                    borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px 8px 28px',
                    fontSize: '15px', fontFamily: 'var(--font-sans)', boxSizing: 'border-box',
                    fontWeight: 600,
                  }}
                />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                {Number(form.speseGestione) > 0
                  ? `Il cliente pagherà €${Number(form.speseGestione).toFixed(2)} in più per ogni prenotazione, mostrate come "Spese di gestione" nel riepilogo.`
                  : 'Imposta 0 per non applicare spese di gestione.'
                }
              </div>
            </div>
          </PanelBody>
        </Panel>
        )}

        {/* ── Modalità prenotazione ── */}
        <Panel>
          <PanelHead
            title="Modalità prenotazione"
            subtitle="Scegli quali modalità di selezione date rendere disponibili ai clienti"
          />
          <PanelBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { key: 'single', label: 'Giornaliero', desc: 'Il cliente seleziona un singolo giorno', icon: '📅' },
                { key: 'range', label: 'Periodo', desc: 'Il cliente seleziona un intervallo di date consecutive', icon: '📆' },
                { key: 'multiple', label: 'Più giorni', desc: 'Il cliente seleziona giorni non consecutivi', icon: '🗓' },
              ].map(({ key, label, desc, icon }) => (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  background: 'var(--surface)', border: `1px solid ${form.modalitaPrenotazione?.[key] ? 'var(--ocean)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-md)', padding: '14px 16px', cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }} onClick={() => setForm(p => ({
                  ...p,
                  modalitaPrenotazione: { ...p.modalitaPrenotazione, [key]: !p.modalitaPrenotazione?.[key] }
                }))}>
                  <span style={{ fontSize: '20px' }}>{icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{desc}</div>
                  </div>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                    border: `2px solid ${form.modalitaPrenotazione?.[key] ? 'var(--ocean)' : 'var(--border-mid)'}`,
                    background: form.modalitaPrenotazione?.[key] ? 'var(--ocean)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {form.modalitaPrenotazione?.[key] && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
              {Object.values(form.modalitaPrenotazione || {}).every(v => !v) && (
                <div style={{ fontSize: '11px', color: '#ef4444', padding: '4px 0' }}>
                  ⚠️ Almeno una modalità deve essere attiva
                </div>
              )}
            </div>
          </PanelBody>
        </Panel>

        {/* ── Politica di cancellazione ── */}
        <Panel>
          <PanelHead
            title="Politica di cancellazione"
            subtitle="Configura come vengono gestiti gli annullamenti e i rimborsi"
          />
          <PanelBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Chi può annullare */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Chi può annullare
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'utente', label: 'Solo utente' },
                    { id: 'admin', label: 'Solo admin' },
                    { id: 'entrambi', label: 'Entrambi' },
                  ].map(opt => (
                    <button key={opt.id}
                      onClick={() => setPolicy('chiPuoAnnullare', opt.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 'var(--radius-md)', fontSize: '12.5px',
                        border: `1px solid ${form.politica.chiPuoAnnullare === opt.id ? 'var(--ocean)' : 'var(--border-mid)'}`,
                        background: form.politica.chiPuoAnnullare === opt.id ? 'rgba(31,147,224,0.15)' : 'transparent',
                        color: form.politica.chiPuoAnnullare === opt.id ? 'var(--ocean)' : 'var(--text-soft)',
                        cursor: 'pointer', fontFamily: 'var(--font-sans)',
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* Tipo rimborso */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Tipo di rimborso
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { id: 'automatico', label: '⚡ Automatico via Stripe' },
                    { id: 'manuale', label: '✋ Manuale' },
                    { id: 'nessuno', label: '🚫 Nessun rimborso' },
                  ].map(opt => (
                    <button key={opt.id}
                      onClick={() => setPolicy('tipoRimborso', opt.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 'var(--radius-md)', fontSize: '12.5px',
                        border: `1px solid ${form.politica.tipoRimborso === opt.id ? 'var(--ocean)' : 'var(--border-mid)'}`,
                        background: form.politica.tipoRimborso === opt.id ? 'rgba(31,147,224,0.15)' : 'transparent',
                        color: form.politica.tipoRimborso === opt.id ? 'var(--ocean)' : 'var(--text-soft)',
                        cursor: 'pointer', fontFamily: 'var(--font-sans)',
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* Soglie rimborso — solo se automatico */}
              {form.politica.tipoRimborso === 'automatico' && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-soft)', marginBottom: '12px' }}>
                    Soglie rimborso automatico
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                        Rimborso completo (giorni prima)
                      </label>
                      <input
                        type="number" min="0"
                        value={form.politica.soglieRimborso?.giorniRimborsoCompleto ?? 7}
                        onChange={e => setPolicySoglia('giorniRimborsoCompleto', e.target.value)}
                        style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                        Rimborso parziale (ore prima)
                      </label>
                      <input
                        type="number" min="0"
                        value={form.politica.soglieRimborso?.oreRimborsoParziale ?? 24}
                        onChange={e => setPolicySoglia('oreRimborsoParziale', e.target.value)}
                        style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                        % rimborso parziale
                      </label>
                      <input
                        type="number" min="0" max="100"
                        value={form.politica.soglieRimborso?.percentualeParziale ?? 50}
                        onChange={e => setPolicySoglia('percentualeParziale', e.target.value)}
                        style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Esempio dinamico */}
                  {(() => {
                    const giorni = form.politica.soglieRimborso?.giorniRimborsoCompleto ?? 7;
                    const ore = form.politica.soglieRimborso?.oreRimborsoParziale ?? 24;
                    const perc = form.politica.soglieRimborso?.percentualeParziale ?? 50;
                    return (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.8, background: 'rgba(31,147,224,0.06)', border: '1px solid rgba(31,147,224,0.15)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--ocean)', marginBottom: '4px' }}>Esempio con i valori inseriti:</div>
                        <div>✅ Cancellazione oltre <strong>{giorni} giorni</strong> prima → rimborso <strong>100%</strong></div>
                        <div>⚠️ Cancellazione entro <strong>{ore} ore</strong> prima → rimborso <strong>{perc}%</strong></div>
                        <div>❌ Cancellazione meno di <strong>{ore} ore</strong> prima → <strong>nessun rimborso</strong></div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Messaggio policy */}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Messaggio policy (mostrato all'utente prima di annullare)
                </label>
                <textarea
                  rows={2}
                  placeholder="es. Le cancellazioni effettuate entro 7 giorni dalla data ricevono rimborso completo."
                  value={form.politica.messaggioPolicy || ''}
                  onChange={e => setPolicy('messaggioPolicy', e.target.value)}
                  style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border-mid)', borderRadius: 'var(--radius-md)', color: '#fff', padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)', resize: 'vertical', minHeight: '60px', boxSizing: 'border-box' }}
                />
              </div>

            </div>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Codice lido" subtitle="Usato internamente per collegare prenotazioni e disponibilità" />
          <PanelBody>
            <div style={{
              fontFamily: 'monospace', fontSize: '13px',
              color: 'var(--ocean)', background: 'var(--surface)',
              padding: '10px 14px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}>
              {admin?.beachCode || '—'}
            </div>
          </PanelBody>
        </Panel>

        {/* ── Stripe Connect ── */}
        <StripeConnectPanel
          beach={beach}
          beachCode={admin?.beachCode}
          refreshBeach={refreshBeach}
        />

      </div>
    </div>
  );
}